import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  catchError,
  from,
  map,
  mergeMap,
  Observable,
  of,
  tap,
  throwError,
} from 'rxjs';
import { SnackBarService } from '@/app/core/services/snack-bar.service';
import { SessionStore } from '@/app/core/state/session-store';
import { PosthogService } from '@/app/core/telemetry/posthog.service';
import {
  API_ERROR_PRESENTATION,
  type ApiErrorPresentationPolicy,
} from './api-error-presentation';

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const CONNECTIVITY_MESSAGE =
  'Unable to reach the server. Check your connection and try again.';

type ApiErrorBody = {
  message?: string;
  byPassGlobalHandler?: boolean;
  errorCode?: string;
  userMessage?: string;
  employeeMessage?: string;
  traceId?: string;
};

type Presentation = {
  message: string;
  usedFallback: boolean;
};

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const policy = req.context.get(API_ERROR_PRESENTATION);
  const snackBar = inject(SnackBarService);
  const session = inject(SessionStore);
  const posthog = inject(PosthogService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        policy.mode === 'feature' ||
        error.status === 401 ||
        error.status === 403
      ) {
        return throwError(() => error);
      }

      if (error.status === 0) {
        presentError(
          { message: CONNECTIVITY_MESSAGE, usedFallback: true },
          error,
          policy,
          snackBar,
          posthog,
          null
        );
        return throwError(() => error);
      }

      return normalizeErrorBody(error.error).pipe(
        tap((body) => {
          if (body?.byPassGlobalHandler) {
            return;
          }

          presentError(
            selectPresentation(body, policy, session.isAsm()),
            error,
            policy,
            snackBar,
            posthog,
            body
          );
        }),
        mergeMap(() => throwError(() => error))
      );
    })
  );
};

function normalizeErrorBody(body: unknown): Observable<ApiErrorBody | null> {
  if (isReadableBlob(body)) {
    return from(body.text()).pipe(
      map((text) => parseBody(text)),
      catchError(() => of(null))
    );
  }

  return of(isApiErrorBody(body) ? body : null);
}

function selectPresentation(
  body: ApiErrorBody | null,
  policy: ApiErrorPresentationPolicy,
  isAsm: boolean
): Presentation {
  if (policy.mode !== 'rich') {
    return {
      message:
        policy.mode === 'generic'
          ? (policy.fallback ?? GENERIC_MESSAGE)
          : GENERIC_MESSAGE,
      usedFallback: true,
    };
  }

  const approvedMessage =
    policy.audience === 'asm' && isAsm
      ? (messageText(body?.employeeMessage) ?? messageText(body?.userMessage))
      : policy.audience === 'all'
        ? messageText(body?.userMessage)
        : undefined;

  return approvedMessage
    ? { message: approvedMessage, usedFallback: false }
    : { message: policy.fallback, usedFallback: true };
}

function messageText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : undefined;
}

function telemetryErrorCode(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const code = value.trim();
  return code.length > 0 &&
    code.length <= 64 &&
    /^[a-z0-9][a-z0-9._-]*$/i.test(code)
    ? code
    : undefined;
}

function presentError(
  presentation: Presentation,
  error: HttpErrorResponse,
  policy: ApiErrorPresentationPolicy,
  snackBar: SnackBarService,
  posthog: PosthogService,
  body: ApiErrorBody | null
): void {
  snackBar.error(presentation.message);
  posthog.capture('api error presented', {
    status: error.status,
    errorCode: telemetryErrorCode(body?.errorCode),
    policy: policy.mode,
    audience: policy.mode === 'rich' ? policy.audience : 'all',
    usedFallback: presentation.usedFallback,
  });
}

function parseBody(text: string): ApiErrorBody | null {
  try {
    const parsed: unknown = JSON.parse(text);
    return isApiErrorBody(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function isReadableBlob(value: unknown): value is Blob {
  return (
    typeof value === 'object' &&
    value !== null &&
    'text' in value &&
    typeof (value as { text: unknown }).text === 'function'
  );
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === 'object' && value !== null;
}
